import parsePhoneNumberFromString from 'libphonenumber-js';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import * as Yup from 'yup';

const IBAN_REGEX =
  /^AL\d{10}[0-9A-Z]{16}$|^AD\d{10}[0-9A-Z]{12}$|^AT\d{18}$|^BH\d{2}[A-Z]{4}[0-9A-Z]{14}$|^BE\d{14}$|^BA\d{18}$|^BG\d{2}[A-Z]{4}\d{6}[0-9A-Z]{8}$|^HR\d{19}$|^CY\d{10}[0-9A-Z]{16}$|^CZ\d{22}$|^DK\d{16}$|^FO\d{16}$|^GL\d{16}$|^DO\d{2}[0-9A-Z]{4}\d{20}$|^EE\d{18}$|^FI\d{16}$|^FR\d{12}[0-9A-Z]{11}\d{2}$|^GE\d{2}[A-Z]{2}\d{16}$|^DE\d{20}$|^GI\d{2}[A-Z]{4}[0-9A-Z]{15}$|^GR\d{9}[0-9A-Z]{16}$|^HU\d{26}$|^IS\d{24}$|^IE\d{2}[A-Z]{4}\d{14}$|^IL\d{21}$|^IT\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^[A-Z]{2}\d{5}[0-9A-Z]{13}$|^KW\d{2}[A-Z]{4}22!$|^LV\d{2}[A-Z]{4}[0-9A-Z]{13}$|^LB\d{6}[0-9A-Z]{20}$|^LI\d{7}[0-9A-Z]{12}$|^LT\d{18}$|^LU\d{5}[0-9A-Z]{13}$|^MK\d{5}[0-9A-Z]{10}\d{2}$|^MT\d{2}[A-Z]{4}\d{5}[0-9A-Z]{18}$|^MR13\d{23}$|^MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}$|^MC\d{12}[0-9A-Z]{11}\d{2}$|^ME\d{20}$|^NL\d{2}[A-Z]{4}\d{10}$|^NO\d{13}$|^PL\d{10}[0-9A-Z]{,16}n$|^PT\d{23}$|^RO\d{2}[A-Z]{4}[0-9A-Z]{16}$|^SM\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^SA\d{4}[0-9A-Z]{18}$|^RS\d{20}$|^SK\d{22}$|^SI\d{17}$|^ES\d{22}$|^SE\d{22}$|^CH\d{7}[0-9A-Z]{12}$|^TN59\d{20}$|^TR\d{7}[0-9A-Z]{17}$|^AE\d{21}$|^GB\d{2}[A-Z]{4}\d{14}$/;

export interface IOnboardingCreateForm {
  name: string;
  address: string;
  city: string;
  postCode: string;
  country: string | null;
  contactEmail: string;
  contactPhone: string;
  iban: string;
  identificationNumber: string;
  taxIdentificationNumber: string;
  valueAddedTaxIdentificationNumber: string;
  servicesType: 'withoutGuarantor' | 'withGuarantor';
  webUrl: string | null;
  guarantor: {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    doctorCode: string;
  };
}

export const initialValues: IOnboardingCreateForm = {
  name: '',
  address: '',
  city: '',
  postCode: '',
  country: null,
  contactEmail: '',
  contactPhone: '',
  iban: '',
  identificationNumber: '',
  taxIdentificationNumber: '',
  valueAddedTaxIdentificationNumber: '',
  servicesType: 'withoutGuarantor',
  webUrl: null,
  guarantor: {
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    doctorCode: '',
  },
};

const emailValidation = Yup.string().required('validation.required').email('validation.email');
const phoneValidation = Yup.string()
  .required('validation.required')
  .test('phone-country', 'validation.phone_format', function (v: any) {
    if (v && v.length !== 4) {
      const phone = parsePhoneNumberFromString(v);
      return phone?.isValid() || false;
    }
    return true;
  });

export const validationSchema = Yup.object().shape({
  name: Yup.string().required('validation.required'),
  address: Yup.string()
    .test('required', 'validation.required_field', (address, { parent }) => {
      const city = parent.city;
      const country_code = parent.country;
      const post_code = parent.postCode;

      if (city || country_code || post_code) {
        return Boolean(address);
      }

      return true;
    })
    .test('validate-street-number', 'validation.address_format', (address) => {
      if (address !== null && address !== undefined) {
        return /\d/.test(address) && /[a-zA-Z]/.test(address);
      }

      return true;
    })
    .max(64, 'validation.max64')
    .nullable()
    .required('validation.required'),
  city: Yup.string()
    .test('required', 'validation.required_field', (city, { parent }) => {
      const address = parent.address;
      const country_code = parent.country;
      const post_code = parent.postCode;

      if (address || country_code || post_code) {
        return Boolean(city);
      }

      return true;
    })
    .max(64, 'validation.max64')
    .nullable()
    .required('validation.required'),
  postCode: Yup.string()
    .test('required', 'validation.required_field', (post_code, { parent }) => {
      const city = parent.city;
      const address = parent.address;
      const country_code = parent.country;

      if (city || address || country_code) {
        return Boolean(post_code);
      }

      return true;
    })
    .test('zip-test', 'validation.zip_format', (zip, testContext) => {
      const country = testContext.parent.country;
      if (!zip || !country || !postcodeValidatorExistsForCountry(country)) {
        return true;
      }

      return postcodeValidator(zip, country);
    })

    .max(16, 'validation.max16')
    .nullable()
    .required('validation.required'),
  country: Yup.mixed().required('validation.required'),
  contactEmail: emailValidation,
  contactPhone: phoneValidation,
  iban: Yup.string()
    .required('validation.required')
    .matches(IBAN_REGEX, { message: 'validation.iban_format' }),
  identificationNumber: Yup.string().required('validation.required'),
  taxIdentificationNumber: Yup.string().required('validation.required'),
  valueAddedTaxIdentificationNumber: Yup.string().nullable(),
  guarantor: Yup.object().when('servicesType', {
    is: (v: string) => v === 'withGuarantor',
    then: (s) =>
      s.required('validation.required').shape({
        email: emailValidation,
        phoneNumber: phoneValidation,
        firstName: Yup.string().required('validation.required'),
        lastName: Yup.string().required('validation.required'),
        doctorCode: Yup.string().required('validation.required'),
      }),
    otherwise: (s) => s.nullable(),
  }),
  webUrl: Yup.string().nullable().url('validation.url'),
});
